import { driver, type Driver } from 'driver.js';
import { defineMessages, type IntlShape } from 'react-intl';
import 'driver.js/dist/driver.css';
import './product-tour.css';
import { resolveSteps } from './resolve-steps';
import { hasSeenTour, markTourSeen } from './tour-storage';
import { TOURS, type TourId } from './tours';
import { waitForAnchor } from './wait-for-anchor';

const messages = defineMessages({
    next: { id: '8935016a-de1b-4a68-94f3-0e17a5c17d0c', defaultMessage: 'Next' },
    previous: { id: 'a751ce79-835b-4084-94fe-a3a4e5d5d140', defaultMessage: 'Back' },
    done: { id: '5e25c28f-db70-427a-994c-ed39bc67c70a', defaultMessage: 'Done' },
    progress: {
        id: '9649c698-6c7f-4387-9827-80b7e6c009d4',
        defaultMessage: '{current} of {total}',
    },
    close: { id: '8feb5459-6f5c-4e3c-b883-49c832ac9a4a', defaultMessage: 'Close the tour' },
});

let start: (() => void) | undefined;
const listeners = new Set<() => void>();

export const hasProductTour = () => !!start;
export function subscribeToProductTour(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

export function startProductTour() {
    start?.();
}

interface TourOptions {
    tourId: TourId;
    intl: IntlShape;
    userId?: string;
    values: Record<string, string>;
}

export function mountProductTour({ tourId, intl, userId, values }: TourOptions) {
    let instance: Driver | undefined;
    const reposition = () => instance?.refresh();
    const run = () => {
        cancelWait();
        instance?.destroy();
        const steps = resolveSteps(TOURS[tourId], {
            intl,
            values,
        });
        if (!steps.length) return;

        instance = driver({
            steps,
            animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
            duration: 260,
            smoothScroll: true,
            allowClose: true,
            stagePadding: 8,
            stageRadius: 16,
            popoverClass: 'cohaven-tour',
            popoverOffset: 12,
            overlayColor: document.documentElement.classList.contains('dark') ? '#15130F' : '#23201B',
            overlayOpacity: 0.55,
            showProgress: true,
            // Preserve the placeholders driver.js interpolates for each step.
            progressText: intl.formatMessage(messages.progress, {
                current: '{{current}}',
                total: '{{total}}',
            }),
            nextBtnText: intl.formatMessage(messages.next),
            prevBtnText: intl.formatMessage(messages.previous),
            doneBtnText: intl.formatMessage(messages.done),
            // driver.js labels its own close button in English.
            onPopoverRender: (popover) => {
                popover.closeButton.setAttribute('aria-label', intl.formatMessage(messages.close));
            },
            onDestroyed: () => document.removeEventListener('scroll', reposition, true),
        });
        document.addEventListener('scroll', reposition, true);
        markTourSeen(userId, tourId);
        instance.drive();
    };

    const cancelWait = hasSeenTour(userId, tourId)
        ? () => {}
        : waitForAnchor(TOURS[tourId][0].anchor, run);
    start = run;
    listeners.forEach((listener) => listener());

    return () => {
        cancelWait();
        instance?.destroy();
        document.removeEventListener('scroll', reposition, true);
        if (start === run) {
            start = undefined;
            listeners.forEach((listener) => listener());
        }
    };
}
